import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AppLoginContainerModel, AppLoginContainerModelUpdate, AppLoginRegistrationContainerModel, AppLoginSubjectContainerModel } from '../../../../../models/aggregates/id';
import { AppleProfile, FacebookProfile, GoogleProfile, LoginRequest } from '../../../../../models/dbschema/auth.model';
import { ForgotPasswordInitRequestDtoModel } from '../../data-transfer-objects/forgot-password-init-request-dto.model';
import { RegistrationRequestDtoModel } from '../../data-transfer-objects/registration-request-dto.model';
import { ResendConfirmationRequestDtoModel } from '../../data-transfer-objects/resend-confirmation-request-dto.model';
import { UserConfirmationRequestModel } from '../../data-transfer-objects/user-confirmation-request.model';
import { ApiResponseModel, BasicApiResponseModel, EcomLoginResponseModel, LoginResponse } from '../shared/api-response.model';
import { ApiVisa } from '../shared/api-visa.model';
import { BaseApiTemplateService } from '../shared/base-api-template-service.model';
import { Core2Config } from '../shared/core2Config';
import { ForgotPwdConfirmRequest, ResetEmail, ResetPassword } from "../shared/login.model";
import { LoginWithOtpInitRequestDtoModel } from '../../data-transfer-objects';
import { AppPasswordlessLoginContainer } from '../../../../../models/aggregates/app-login';

@Injectable({providedIn: 'root'})
export class AuthService extends BaseApiTemplateService<any> {

  constructor(http: HttpClient) {
    super();
    this.apiUrl = Core2Config.DOMAIN_URL + Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/login';
    this.endpoint_path = Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/login';
    this.httpClient = http;
  }

  loginByPassword(requestData: LoginRequest, visa: ApiVisa): Observable<BasicApiResponseModel<any>> {
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = visa.api_domain_url + this.endpoint_path;
    }
    return this.httpClient.post<BasicApiResponseModel<any>>(this.apiUrl, requestData, {withCredentials: true});
  }

  loginWithGoogle(req: GoogleProfile, visa: ApiVisa) {
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = `${visa.api_domain_url}${this.endpoint_path}/google`;
    }
    return this.httpClient.post<BasicApiResponseModel<LoginResponse>>(this.apiUrl, req)
  }

  loginWithFaceBook(req: FacebookProfile, visa: ApiVisa) {
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = `${visa.api_domain_url}${this.endpoint_path}/facebook`;
    }
    return this.httpClient.post<BasicApiResponseModel<LoginResponse>>(this.apiUrl, req)
  }

  loginWithAzure(visa: ApiVisa){
    if (this.endpoint_path && visa.api_domain_url) {
      if(visa.tenantCode){
        this.apiUrl = `${visa.api_domain_url}` + Core2Config.TENANT_DOMAIN_URL_PREFIX + 'identity/login/auth/microsoft/sso/saml-init-login/' + visa.tenantCode;
      }
      else{
        this.apiUrl = `${visa.api_domain_url}` + Core2Config.TENANT_DOMAIN_URL_PREFIX + 'identity/login/auth/microsoft/sso/saml-init-login';
      }
    }
    return this.httpClient.post<ApiResponseModel<any>>(this.apiUrl, {})
  }

  loginWithApple(req: AppleProfile, visa: ApiVisa) {
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = `${visa.api_domain_url}${this.endpoint_path}/apple`;
    }
    return this.httpClient.post<BasicApiResponseModel<LoginResponse>>(this.apiUrl, req)
  }

  ecomloginByPassword(requestData: LoginRequest, visa: ApiVisa): Observable<EcomLoginResponseModel<any>> {
    this.apiUrl = Core2Config.DOMAIN_URL + Core2Config.TENANT_ECOM_URL_PREFIX + 'identity/login';
    this.endpoint_path = Core2Config.TENANT_ECOM_URL_PREFIX + 'identity/login';
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = visa.api_domain_url + this.endpoint_path;
    }
    return this.httpClient.post<EcomLoginResponseModel<any>>(this.apiUrl, requestData, {...this.getHttpHeader(visa), withCredentials: true});
  }

  loginWithOtpInitWithJwtToken(requestData: LoginWithOtpInitRequestDtoModel, jwtToken: string, visa: ApiVisa): Promise<BasicApiResponseModel<AppPasswordlessLoginContainer>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/password-less/login/with-token/otp_init/public-ep?tenantCode=' + visa.tenantCode;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'authorization': jwtToken,
      })
    };
    return this.httpClient.post<BasicApiResponseModel<AppPasswordlessLoginContainer>>(
      apiUrl,
      requestData,
      httpOptions
    ).toPromise();
  }

  register(requestData: RegistrationRequestDtoModel, visa: ApiVisa): Promise<BasicApiResponseModel<AppLoginContainerModel>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/registration';
    console.log('Register New User');
    return this.httpClient.post<BasicApiResponseModel<AppLoginContainerModel>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  registerTenant(requestData: RegistrationRequestDtoModel, visa: ApiVisa): Observable<BasicApiResponseModel<AppLoginContainerModel>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl +=  Core2Config.TENANT_DOMAIN_URL_PREFIX + 'identity/registration/basic?tenantCode=' + visa.tenantCode;
    console.log('Register New' + visa.tenantCode +'User');
    return this.httpClient.post<BasicApiResponseModel<AppLoginContainerModel>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    );
  }

  ecomRegister(requestData: RegistrationRequestDtoModel, visa: ApiVisa, followExistingWorkflow?: boolean, websiteCode?: string): Promise<BasicApiResponseModel<AppLoginContainerModel>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.TENANT_ECOM_URL_PREFIX + 'identity/registration?tenantCode=' + visa.tenantCode;

    if (!followExistingWorkflow && websiteCode) {
      apiUrl += '&followExistingWorkflow=' + followExistingWorkflow + '&websiteCode=' + websiteCode;
    }
    console.log('Register New ECOM User');
    return this.httpClient.post<BasicApiResponseModel<AppLoginContainerModel>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  registerWithGoogle(req: GoogleProfile, visa: ApiVisa, tenantCode?: string) {
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = `${visa.api_domain_url}`;
    }
    this.apiUrl += `${Core2Config.PLATFORM_URL_PREFIX}${Core2Config.DOMAIN_PREFIX}identity/registration/google`;
    const params = tenantCode ? new HttpParams().set('tenantCode', tenantCode) : new HttpParams().set('', '');
    return this.httpClient.post<BasicApiResponseModel<AppLoginContainerModelUpdate>>(this.apiUrl, req, {params})
  }

  registerWithFacebook(req: FacebookProfile, visa: ApiVisa, tenantCode?: string) {
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = `${visa.api_domain_url}`;
    }
    this.apiUrl += `${Core2Config.PLATFORM_URL_PREFIX}${Core2Config.DOMAIN_PREFIX}identity/registration/facebook`;
    const params = tenantCode ? new HttpParams().set('tenantCode', tenantCode) : new HttpParams().set('', '');
    return this.httpClient.post<BasicApiResponseModel<AppLoginContainerModelUpdate>>(this.apiUrl, req, {params})
  }

  getTenantUserProfile(visa: ApiVisa): Observable<any> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }

    apiUrl += Core2Config.TENANT_DOMAIN_URL_PREFIX + 'user-profile?tenantCode=' + visa.tenantCode;
    return this.httpClient.get<BasicApiResponseModel<AppLoginSubjectContainerModel>>(
      apiUrl,
      this.getHttpHeader(visa)
    );
  }

  forgotPasswordInit(requestData: ForgotPasswordInitRequestDtoModel, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/password/reset_init';
    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  forgotPasswordWithJwtToken(requestData: ForgotPasswordInitRequestDtoModel, jwtToken: string, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/password/with-token/reset_init/public-ep';
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'authorization': jwtToken,
      })
    };
    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      requestData,
      httpOptions
    ).toPromise();
  }

  forgotPasswordEcom(requestData: ForgotPasswordInitRequestDtoModel, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/password/reset_init?tenantCode=' + visa.tenantCode;
    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  forgotPasswordEcomWithJwtToken(requestData: ForgotPasswordInitRequestDtoModel, jwtToken: string, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/password/with-token/reset_init/public-ep?tenantCode=' + visa.tenantCode;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'authorization': jwtToken,
      })
    };
    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      requestData,
      httpOptions
    ).toPromise();
  }

  forgotPasswordConfirm(requestData: ForgotPwdConfirmRequest, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/password/reset_confirm';
    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }


  forgotPasswordConfirmWithToken(requestData: ForgotPwdConfirmRequest, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/password/with-token/reset_confirm/public-ep';
    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  changePassword(requestData: ResetPassword, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/password/change';
    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  changePasswordWithoutAuth(requestData: ResetPassword, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/password/change_without_auth';
    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  changeEmail(requestData: ResetEmail, visa: ApiVisa): Promise<ApiResponseModel<AppLoginSubjectContainerModel>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/userprofile/reset-email';
    return this.httpClient.post<ApiResponseModel<AppLoginSubjectContainerModel>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  resendConfirmation(email: string, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }

    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/registration/resend_confirmation';
    const requestData = {email};
    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  resendConfirmationEmail(requestData: ResendConfirmationRequestDtoModel, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }

    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/registration/resend_confirmation?tenantCode=' + visa.tenantCode;
    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  verifyEmail(email: string, code: string, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX +
      'identity/registration/user_confirmation?email=' + encodeURIComponent(email) + '&code=' + code;

    return this.httpClient.get<ApiResponseModel<any>>(
      apiUrl,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  phoneVerify(number: string, messageTemplate: string, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX +
      'identity/registration/phoneverify?number=' + number + '&tenantCode=' + visa.tenantCode + '&messageTemplate=' + messageTemplate;
    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  phoneVerifyConfirmation(code: string, number: string, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }

    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX +
      'identity/registration/verify_confirmation?number=' + number + '&code=' + code;
    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  getPermission(guid: string, visa: ApiVisa) {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    const URL = apiUrl + `core1/perm-assignee-target-links/query?perm-dfn-guid=C95A86C8-2E1D-443D-B32B-B82A45045863&assignee-hdr-guid=${guid}`;
    // return this.httpClient.get(URL, this.getHttpHeader(visa));
    return this.httpClient.get(URL, this.getHttpHeader(visa));

  }

  /**
   * @deprecated
   * @param please donnt use this method
   */
  getProfile(visa: ApiVisa) {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    const URL = apiUrl + 'core1/userprofile';
    // const URL = Core2Config.DOMAIN_URL + Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/userprofile';
    return this.httpClient.get(URL, this.getHttpHeader(visa));
    // return this.httpClient.get<BasicApiResponseModel<AppLoginContainerModel>>(
    //   this.apiUrl,
    //   this.getHttpHeader(visa)
    // );
  }

  /* Registration V2 start */
  registerEmailV2(requestData: RegistrationRequestDtoModel, visa: ApiVisa, tenantCode?: string, websiteCode?: string, inviteUrlKey?: string): Promise<BasicApiResponseModel<AppLoginRegistrationContainerModel>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }

    const tenantCodeQueryString = (tenantCode ? 'tenantCode='+encodeURIComponent(tenantCode): '');
    const websiteCodeQueryString = (websiteCode ? 'websiteCode='+encodeURIComponent(websiteCode): '');
    const inviteUrlKeyQueryString = (inviteUrlKey ? 'inviteKey='+inviteUrlKey: '');

    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX +
      'identity/registration/email?' + tenantCodeQueryString + '&' + websiteCodeQueryString + '&' + inviteUrlKeyQueryString;

    return this.httpClient.post<BasicApiResponseModel<AppLoginRegistrationContainerModel>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  registerMobileNumberV2(requestData: RegistrationRequestDtoModel, visa: ApiVisa, tenantCode?: string, websiteCode?: string, inviteUrlKey?: string): Promise<BasicApiResponseModel<AppLoginRegistrationContainerModel>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }

    const tenantCodeQueryString = (tenantCode ? 'tenantCode='+encodeURIComponent(tenantCode): '');
    const websiteCodeQueryString = (websiteCode ? 'websiteCode='+encodeURIComponent(websiteCode): '');
    const inviteUrlKeyQueryString = (inviteUrlKey ? 'inviteKey='+inviteUrlKey: '');

    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX +
      'identity/registration/mobile_number?' + tenantCodeQueryString + '&' + websiteCodeQueryString + '&' + inviteUrlKeyQueryString;

    return this.httpClient.post<BasicApiResponseModel<AppLoginRegistrationContainerModel>>(
      apiUrl,
      requestData,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  /* Registration WithJwtToken start */
  // generateJwtTokenWithRecaptha(apiVisa: ApiVisa, gRecaptchaResponseToken: string): Observable<BasicApiResponseModel<string>> {
  //   let apiUrl: string = Core2Config.DOMAIN_URL;
  //   const recaptha = (gRecaptchaResponseToken ? 'gRecaptchaResponseToken=' + gRecaptchaResponseToken : '');
  //   if (apiVisa.api_domain_url) {
  //     apiUrl = apiVisa.api_domain_url;
  //   }
  //   apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX +
  //     'identity/registration/generate-token/public-ep?' + recaptha;
  //   apiVisa.applet_code = apiVisa.applet_code ? apiVisa.applet_code : 'none';
  //   apiVisa.tenantCode = apiVisa.tenantCode
  //     ? apiVisa.tenantCode
  //     : '';
  //
  //   const httpOptions = {
  //     headers: new HttpHeaders({
  //       'Content-Type': 'application/json',
  //     })
  //   };
  //   return this.httpClient.get<BasicApiResponseModel<string>>(apiUrl, httpOptions);
  // }

  generateJwtTokenWithRecaptha(
    apiVisa: ApiVisa,
    gRecaptchaResponseToken: string,
    captchaType?: 'google' | 'tencent',
    randStr?: string
  ): Observable<BasicApiResponseModel<string>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;

    const params = new URLSearchParams();
    if (gRecaptchaResponseToken) {
      params.append('gRecaptchaResponseToken', gRecaptchaResponseToken);
    }
    if (captchaType) {
      params.append('captchaType', captchaType);
    }
    if (randStr) {
      params.append('randstr', randStr);
    }

    if (apiVisa.api_domain_url) {
      apiUrl = apiVisa.api_domain_url;
    }

    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX +
      'identity/registration/generate-token/public-ep?' + params.toString();

    apiVisa.applet_code = apiVisa.applet_code || 'none';
    apiVisa.tenantCode = apiVisa.tenantCode || '';

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };

    return this.httpClient.get<BasicApiResponseModel<string>>(apiUrl, httpOptions);
  }


  generateJwtTokenWithoutRecaptha(apiVisa: ApiVisa): Observable<BasicApiResponseModel<string>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (apiVisa.api_domain_url) {
      apiUrl = apiVisa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX +
      'identity/registration/generate-token-without-captcha/public-ep?';
    apiVisa.applet_code = apiVisa.applet_code ? apiVisa.applet_code : 'none';
    apiVisa.tenantCode = apiVisa.tenantCode
      ? apiVisa.tenantCode
      : '';

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };
    return this.httpClient.get<BasicApiResponseModel<string>>(apiUrl, httpOptions);
  }

  registerEmailWithJwtToken(requestData: RegistrationRequestDtoModel, visa: ApiVisa, tenantCode?: string, websiteCode?: string, inviteUrlKey?: string, jwtToken?: string): Promise<BasicApiResponseModel<AppLoginRegistrationContainerModel>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }

    const tenantCodeQueryString = (tenantCode ? 'tenantCode=' + encodeURIComponent(tenantCode) : '');
    const websiteCodeQueryString = (websiteCode ? 'websiteCode=' + encodeURIComponent(websiteCode) : '');
    const inviteUrlKeyQueryString = (inviteUrlKey ? 'inviteKey='+inviteUrlKey: '');

    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX +
      'identity/registration/with-token/email/public-ep?' + tenantCodeQueryString + '&' + websiteCodeQueryString + '&' + inviteUrlKeyQueryString;

      visa.applet_code = visa.applet_code ? visa.applet_code : 'none';
    visa.tenantCode = visa.tenantCode
      ? visa.tenantCode
      : '';
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'authorization': jwtToken,
      })
    };
    return this.httpClient.post<BasicApiResponseModel<AppLoginRegistrationContainerModel>>(
      apiUrl,
      requestData,
      httpOptions
    ).toPromise();
  }

  registerMobileWithJwtToken(requestData: RegistrationRequestDtoModel, visa: ApiVisa, tenantCode?: string, websiteCode?: string, inviteUrlKey?: string, jwtToken?: string): Promise<BasicApiResponseModel<AppLoginRegistrationContainerModel>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }

    const tenantCodeQueryString = (tenantCode ? 'tenantCode=' + encodeURIComponent(tenantCode) : '');
    const websiteCodeQueryString = (websiteCode ? 'websiteCode=' + encodeURIComponent(websiteCode) : '');
    const inviteUrlKeyQueryString = (inviteUrlKey ? 'inviteKey='+inviteUrlKey: '');

    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX +
      'identity/registration/with-token/mobile-number/public-ep?' + tenantCodeQueryString + '&' + websiteCodeQueryString + '&' + inviteUrlKeyQueryString;

    visa.applet_code = visa.applet_code ? visa.applet_code : 'none';
    visa.tenantCode = visa.tenantCode
      ? visa.tenantCode
      : '';
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'authorization': jwtToken,
      })
    };
    return this.httpClient.post<BasicApiResponseModel<AppLoginRegistrationContainerModel>>(
      apiUrl,
      requestData,
      httpOptions
    ).toPromise();
  }

  userConfirmationV2(userConfirmationRequest: UserConfirmationRequestModel, visa: ApiVisa): Promise<ApiResponseModel<any>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX + 'identity/registration/user_confirmation_new';

    return this.httpClient.post<ApiResponseModel<any>>(
      apiUrl,
      userConfirmationRequest,
      this.getHttpHeader(visa)
    ).toPromise();
  }

  resendConfirmationEmailV2(requestData: ResendConfirmationRequestDtoModel, visa: ApiVisa, tenantCode?: string, jwtToken?: string): Promise<BasicApiResponseModel<AppLoginRegistrationContainerModel>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }

    const tenantCodeQueryString = (tenantCode ? 'tenantCode=' + encodeURIComponent(tenantCode) : '');
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX +
      'identity/registration/resend_confirmation_email?' + tenantCodeQueryString;

    visa.applet_code = visa.applet_code ? visa.applet_code : 'none';
    visa.tenantCode = visa.tenantCode
      ? visa.tenantCode
      : '';
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'authorization': jwtToken,
      })
    };
    return this.httpClient.post<BasicApiResponseModel<AppLoginRegistrationContainerModel>>(
      apiUrl,
      requestData,
      httpOptions
    ).toPromise();
  }

  resendConfirmationMobileNumberV2(requestData: ResendConfirmationRequestDtoModel, visa: ApiVisa, tenantCode?: string, jwtToken?: string): Promise<BasicApiResponseModel<AppLoginRegistrationContainerModel>> {
    let apiUrl: string = Core2Config.DOMAIN_URL;
    if (visa.api_domain_url) {
      apiUrl = visa.api_domain_url;
    }

    const tenantCodeQueryString = (tenantCode ? 'tenantCode=' + encodeURIComponent(tenantCode) : '');
    apiUrl += Core2Config.PLATFORM_URL_PREFIX + Core2Config.DOMAIN_PREFIX +
      'identity/registration/resend_confirmation_mobile_number?' + tenantCodeQueryString;

    visa.applet_code = visa.applet_code ? visa.applet_code : 'none';
    visa.tenantCode = visa.tenantCode
      ? visa.tenantCode
      : '';
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'authorization': jwtToken,
      })
    };

    return this.httpClient.post<BasicApiResponseModel<AppLoginRegistrationContainerModel>>(
      apiUrl,
      requestData,
      httpOptions
    ).toPromise();
  }
  /* Registration V2 end */

  loginWithNoAppletLoading(requestData: LoginRequest, visa: ApiVisa): Observable<BasicApiResponseModel<any>> {
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = `${visa.api_domain_url}${this.endpoint_path}/no-applet-loading`;
    }
    return this.httpClient.post<BasicApiResponseModel<any>>(this.apiUrl, requestData, {withCredentials: true});
  }
}

